import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchWarehouseDropdown } from "slices/Dropdowns/Warehouse/WarehouseDropdown/thunk";
import {
  exportInStockCSV,
  fetchInStock,
} from "slices/Warehouse/ProjectWarehouse/InStock/thunk";
import { clearInStock } from "slices/Warehouse/ProjectWarehouse/InStock/reducer";
import VisibilityIcon from "@mui/icons-material/Visibility";

type InstockProps = {
  warehouseId: string;
};
const Instock: React.FC<InstockProps> = ({ warehouseId }) => {
  document.title = "Warehouse Instock";

  const tableState = usePreservedTableState({
    key: TableKeys.WAREHOUSE.STOCK_OVERVIEW.IN_STOCK,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [data, setData] = useState([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectInStock = createSelector(
    (state: any) => state.WarehouseInStock,
    (item) => ({
      list: item?.list || [],
      rowCount: item?.rowCount || 0,
      loading: item?.loading ?? false,
      error: item?.error ?? null,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(selectInStock);

  const { canView } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.stock_overview,
  );

  const { selectedWarehouse } = useSelector(
    createSelector(
      (state: any) => state.warehouseDropdownReducer,
      (data) => ({
        selectedWarehouse: data.selected,
      }),
    ),
  );

  // Initial Listing Dispatch
  useEffect(() => {
    if (warehouseId) {
      dispatch(
        fetchInStock({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
          warehouse_id: warehouseId,
        }),
      );
    } else {
      dispatch(clearInStock());
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    sortField,
    sortDirection,
    warehouseId,
  ]);

  useEffect(() => {
    setData(list);
  }, [list]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchWarehouseDropdown());
  }, [dispatch]);

  // Filter menu items to check if any will be visible
  // This ensures we only show the Action column if there are visible menu items
  const DynamicAction = canView
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const data = row.original;
            const menuItems = [
              {
                icon: <VisibilityIcon fontSize="small" color="info" />,
                label: "View",
                permission: canView,
                link: (item: any) =>
                  `/warehouse-stock-overview-instock-item-details/${warehouseId}/${
                    data.product_id || data.product?.id
                  }`,
                color: "primary",
              },
            ].filter((item) => item.permission === true);
            return <DynamicMenuItem row={data} menuItems={menuItems} />;
          },
        },
      ]
    : [];

  const columns = useMemo(() => {
    return [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Product Name",
        accessorKey: "item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "item_name",
            warehouseId,
          ),
      },
      {
        header: "Product SKU",
        accessorKey: "product.product_sku",
        id: "item_sku",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "item_sku",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) =>
          row.original.product?.product_sku || "-",
      },
      {
        header: "Make",
        accessorKey: "product.make",
        id: "make",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "make",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.make || "-",
      },
      {
        header: "Model",
        accessorKey: "product.model",
        id: "model",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "model",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.model || "-",
      },
      {
        header: "Unit",
        accessorKey: "product.unit",
        id: "unit",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "unit",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.unit || "-",
      },
      {
        header: "Good Quantity",
        accessorKey: "robust",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "robust",
            warehouseId,
          ),
      },
      {
        header: "Damage Quantity",
        accessorKey: "damage",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "damage",
            warehouseId,
          ),
      },
      {
        header: "Total Quantity",
        accessorKey: "total_quantity",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "total_quantity",
            warehouseId,
          ),
      },
      {
        header: "Category",
        accessorKey: "product.category",
        id: "category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.category || "-",
      },
      {
        header: "Sub Category",
        accessorKey: "product.sub_category",
        id: "sub_category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.IN_STOCK?.DROPDOWN(
            "sub_category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) =>
          row.original.product?.sub_category || "-",
      },

      ...DynamicAction,
    ];
  }, [dispatch, list, navigate, canView, warehouseId]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            <ExportButton
              onExport={async () => {
                return await exportInStockCSV({
                  warehouse_id: warehouseId,
                  search,
                  filters: filterValue,
                });
              }}
            />
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <LoaderErrorWrapper loading={loading} error={error}>
            <MasterTableContainer
              columns={columns}
              data={data || []}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              manualSorting={true}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        )}
      </div>
    </div>
  );
};

export default Instock;
